import { Button } from '@cian/ui-kit';
import { IButtonProps } from '@cian/ui-kit/button/types';
import { IconCommunicationCall16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackFlatTourBookingButtonClick, trackFlatTourBookingSuccess } from './tracking';
import { addFlatTourBooking, getFlatTourSchedule } from '../../../browser/services/flatTourBooking';
import {
  getButtonText,
  getIsFlatTourBookingAvailable,
  getIsFlatTourBookingEnabled,
} from '../../selectors/newbuildingFlatTourBooking';
import { getNewbuildingId, getName } from '../../selectors/newbuildingHeader';
import { TThunkDispatch } from '../../types/redux';
import { NewbuildingFlatTourBookingModalContainerLoadable } from '../NewbuildingFlatTourBookingModal';

import type { ICallbackParams } from '@cian/newbuilding-flat-view-order-component';

interface INewbuildingFlatTourBookingContainerProps {
  isShortened: boolean;
}

export const NewbuildingFlatTourBookingContainer: React.FC<INewbuildingFlatTourBookingContainerProps> = ({
  isShortened,
}) => {
  const [isPopupOpened, setPopupVisibility] = React.useState<boolean>(false);

  const dispatch = useDispatch<TThunkDispatch>();

  const isEnabled = useSelector(getIsFlatTourBookingEnabled);
  const isFlatTourBookingAvailable = useSelector(getIsFlatTourBookingAvailable);
  const buttonText = useSelector(getButtonText);
  const newbuildingName = useSelector(getName) || '';
  const newbuildingId = useSelector(getNewbuildingId);

  React.useEffect(() => {
    if (isEnabled) {
      dispatch(getFlatTourSchedule());
    }
  }, [isEnabled, dispatch]);

  const handleClick = React.useCallback(() => {
    setPopupVisibility(true);

    if (newbuildingId) {
      trackFlatTourBookingButtonClick(newbuildingId);
    }
  }, [newbuildingId]);

  const handleClose = React.useCallback(() => {
    setPopupVisibility(false);
  }, []);

  const handleSuccess = React.useCallback(
    (params: ICallbackParams) => {
      if (newbuildingId) {
        trackFlatTourBookingSuccess(newbuildingId, params);
      }
    },
    [newbuildingId],
  );

  const handleSubmit = React.useCallback(
    async (params: ICallbackParams) => {
      try {
        const parameters = {
          phone: params.phone,
          tourTime: `${params.date} ${params.time}`,
          fullName: params.name,
          newbuildingId: newbuildingId as number,
        };

        // eslint-disable-next-line @typescript-eslint/await-thenable
        await dispatch(addFlatTourBooking(parameters));

        return true;
      } catch {
        return false;
      }
    },
    [newbuildingId, dispatch],
  );

  if (!isEnabled || !buttonText) {
    return null;
  }

  const baseProps: IButtonProps = {
    theme: 'stroke_primary',
    size: 'M',
    disabled: !isFlatTourBookingAvailable,
    onClick: handleClick,
  };

  const button = isShortened ? (
    <Button
      {...baseProps}
      beforeIcon={
        <IconCommunicationCall16 color={isFlatTourBookingAvailable ? 'icon-main-default' : 'icon-main-disabled'} />
      }
    />
  ) : (
    <Button {...baseProps}>{buttonText}</Button>
  );

  return (
    <>
      {button}
      <NewbuildingFlatTourBookingModalContainerLoadable
        newbuildingName={newbuildingName}
        isOpen={isPopupOpened}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onClose={handleClose}
      />
    </>
  );
};
